import * as React from 'react';

// @ts-expect-error - imported from .js/.jsx file
import {HtmlAttachmentIcon} from '../icon/svg/html-attachment-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlBoldIcon} from '../icon/svg/html-bold-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlImageIcon} from '../icon/svg/html-image-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlItalicIcon} from '../icon/svg/html-italic-icon';
import {HtmlLinkIcon} from '../icon/svg/html-link-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlListIcon} from '../icon/svg/html-list-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlUnderlineIcon} from '../icon/svg/html-underline-icon';

export type ToolbarOption =
    | 'bold'
    | 'italic'
    | 'underline'
    | 'link'
    | 'bulletList'
    | 'numberedList'
    | 'image'
    | 'attachment'
    | 'placeholder';

export type ToolbarConfig = {
    options: ToolbarOption[];
    // Optional custom handlers
    onLinkClick?: () => void;
    onAttachmentClick?: () => void;
    // Styling options
    className?: string;
    buttonSize?: number;
    alignCenter?: boolean;
    disabled?: boolean;
    // Placeholder select options
    placeholderOptions?: Array<{
        value: string;
        label: string;
    }>;
    allowCustomPlaceholders?: boolean;
};

type Props = {
    id: string;
    config: ToolbarConfig;
    // Optional additional children to render after the toolbar buttons
    children?: React.ReactNode;
};

/* These are magic classNames that ReactQuill
will pick up and turn into interactive buttons
with icons - don't change them! */
const TOOLBAR_BUTTON_CONFIGS = {
    bold: {
        className: 'ql-bold',
        icon: HtmlBoldIcon,
        title: 'Bold',
    },
    italic: {
        className: 'ql-italic',
        icon: HtmlItalicIcon,
        title: 'Italic',
    },
    underline: {
        className: 'ql-underline',
        icon: HtmlUnderlineIcon,
        title: 'Underline',
    },
    link: {
        className: 'ql-link',
        icon: HtmlLinkIcon,
        title: 'Link',
    },
    bulletList: {
        className: 'ql-list',
        icon: HtmlListIcon,
        title: 'Bullet List',
        value: 'bullet',
    },
    numberedList: {
        className: 'ql-list',
        icon: HtmlListIcon,
        title: 'Numbered List',
        value: 'ordered',
    },
    image: {
        className: 'ql-image',
        icon: HtmlImageIcon,
        title: 'Image',
    },
    attachment: {
        className: 'ql-attachment',
        icon: HtmlAttachmentIcon,
        title: 'Attachment',
    },
} as const;

export function QuillToolbar(props: Props) {
    const {config} = props;
    const buttonSize = config.buttonSize || 14;
    const alignCenter = config.alignCenter !== false; // Default to true

    const renderToolbarButton = (option: ToolbarOption) => {
        // Skip placeholder option as it's handled separately
        if (option === 'placeholder') {
            return null;
        }

        const buttonConfig = TOOLBAR_BUTTON_CONFIGS[option];
        if (!buttonConfig) {
            return null;
        }

        const IconComponent = buttonConfig.icon;
        const buttonProps: {
            className: string;
            disabled?: boolean;
            title: string;
            key: string;
            value?: string;
            onClick?: () => void;
        } = {
            className: buttonConfig.className,
            disabled: config.disabled,
            title: buttonConfig.title,
            key: option,
        };

        // @ts-expect-error
        if (buttonConfig.value) {
            // @ts-expect-error
            buttonProps.value = buttonConfig.value;
        }

        // Add custom click handler if provided
        if (option === 'link' && config.onLinkClick) {
            buttonProps.onClick = config.onLinkClick;
        } else if (option === 'attachment' && config.onAttachmentClick) {
            buttonProps.onClick = config.onAttachmentClick;
        }

        return (
            <button {...buttonProps}>
                <IconComponent size={buttonSize} alignCenter={alignCenter} />
            </button>
        );
    };

    const renderPlaceholderSelect = () => {
        if (!config.placeholderOptions) {
            return null;
        }

        return (
            <select className='ql-placeholder' disabled={config.disabled} key='placeholder'>
                {config.placeholderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
                {config.allowCustomPlaceholders && <option value='placeholder'>Placeholder</option>}
            </select>
        );
    };

    return (
        <div id={props.id} className={config.className}>
            {config.options.map((option) => {
                if (option === 'placeholder') {
                    return renderPlaceholderSelect();
                }

                return renderToolbarButton(option);
            })}
            {props.children}
        </div>
    );
}
